import { PropsWithChildren } from 'react';

import { ActionBarProps } from './action-bar.types';
import { GroupAction } from './actions/group';
import { BatchAction } from './batch-action';
import { FormattedNumber } from '../formatted-number/formatted-number';
import { Tooltip } from '../tooltip/tooltip';

const wrapperClasses = 'p-2 border-b border-b-gray-300 border-solid bg-white h-14 text-sm leading-6 flex items-center';
const countDisplayClasses = 'ml-2 mr-8';
const skeletonClasses = 'h-6 w-25 bg-gray-100 rounded-lg mb-1';

const getShowBatchActions = (numSelected: number, isLoading: boolean) => {
  return numSelected > 0 && !isLoading;
};

const CountDisplay = ({ count, numSelected = 0, isLoading, itemText, selectionHelperText }: ActionBarProps) => {
  return isLoading ? (
    <div data-testid="skeleton" className={skeletonClasses} />
  ) : (
    <div className={countDisplayClasses}>
      {numSelected > 0 ? (
        <>
          <FormattedNumber>{numSelected}</FormattedNumber> {selectionHelperText ?? 'selected'}
        </>
      ) : (
        <>
          <FormattedNumber>{count}</FormattedNumber> {itemText}
        </>
      )}
    </div>
  );
};

const fileExportStart = (fileExportCallback?: () => void) => {
  if (fileExportCallback) fileExportCallback();
};

const ExportBatchAction = ({ isLoading, fileExportCallback, showExportResults }: ActionBarProps) => {
  return isLoading || !showExportResults ? (
    <></>
  ) : (
    <BatchAction
      type="export"
      onClick={() => {
        fileExportStart(fileExportCallback);
      }}
    />
  );
};

const ImportBatchAction = ({ isLoading, importCallback, showImport }: ActionBarProps) => {
  return isLoading || !showImport ? (
    <></>
  ) : (
    <BatchAction
      type="import"
      onClick={() => {
        if (importCallback) importCallback();
      }}
    />
  );
};

const customizeClick = (customizeCallback: ActionBarProps['customizeCallback']) => () => {
  if (customizeCallback) {
    customizeCallback();
  }
};

const CustomizeButton = ({ numSelected = 0, isLoading, showCustomize, customizeCallback }: ActionBarProps) => {
  const showCustomizeAction = !showCustomize || (numSelected > 0 && !isLoading);

  return showCustomizeAction ? <></> : <BatchAction type="customize" onClick={customizeClick(customizeCallback)} />;
};

const SaveViewButton = ({ numSelected = 0, isLoading, showSaveView, onSaveView }: ActionBarProps) => {
  const handleSaveViewClick = () => {
    if (onSaveView) {
      onSaveView();
    }
  };

  return (numSelected > 0 && !isLoading) || !showSaveView ? (
    <></>
  ) : (
    <BatchAction type="saveView" onClick={handleSaveViewClick} />
  );
};

export const ActionBar = ({
  numSelected = 0,
  isLoading = false,
  children,
  showGroup,
  groupProps,
  itemText = 'items',
  count = 0,
  showCustomize = true,
  showExportResults = true,
  ...props
}: PropsWithChildren<ActionBarProps>) => {
  const showBatchActions = getShowBatchActions(numSelected, isLoading);

  return (
    <div className={wrapperClasses}>
      <CountDisplay {...props} numSelected={numSelected} isLoading={isLoading} itemText={itemText} count={count} />
      <ImportBatchAction {...props} isLoading={isLoading} />
      <ExportBatchAction {...props} isLoading={isLoading} showExportResults={showExportResults} />
      {showBatchActions ? children : <></>}
      <div className="ml-auto flex">
        <Tooltip
          title="Save current view of columns. Filters are not included."
          innerContentClassName="whitespace-nowrap"
          overlayStyle={{ maxWidth: 'none', minWidth: 'fit-content', width: 'auto' }}
          autoPlacementOptions={{
            allowedPlacements: ['bottom-start', 'bottom-end'],
          }}
        >
          {/* div is needed for tooltip to work*/}
          <div>
            <SaveViewButton {...props} numSelected={numSelected} isLoading={isLoading} />
          </div>
        </Tooltip>
        {showGroup && groupProps && !isLoading && <GroupAction {...groupProps} />}
        <CustomizeButton {...props} numSelected={numSelected} isLoading={isLoading} showCustomize={showCustomize} />
      </div>
    </div>
  );
};

export default ActionBar;
